import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Avatar,
    Stack, Button, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TextField, InputAdornment, MenuItem,
    CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Alert, TableSortLabel, Autocomplete, Divider
} from '@mui/material';
import {
    Search, FileDownload, Add
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import { saveAs } from 'file-saver';
import { pdf } from "@react-pdf/renderer";

// Project Components & Assets
import Header from "../../component/header/Header";
import ReceiptPDF from "./receipt";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037'
};

const monthList = [
    { id: "January", name: "January" }, { id: "February", name: "February" },
    { id: "March", name: "March" }, { id: "April", name: "April" },
    { id: "May", name: "May" }, { id: "June", name: "June" },
    { id: "July", name: "July" }, { id: "August", name: "August" },
    { id: "September", name: "September" }, { id: "October", name: "October" },
    { id: "November", name: "November" }, { id: "December", name: "December" }
];

const CURRENT_YEAR = new Date().getFullYear();
const yearList = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const API_BASE = "https://www.geniusmileniaeducation.link/db-api";

const calcTotal = (children = [], receiptType) => children.reduce(
    (sum, child) => sum + (receiptType === 'registration' ? Number(child.amount) || 0 : Math.round(child.amount || 0)),
    0
);

const ReceiptListScreen = () => {
    const navigation = useNavigate();

    // --- Data ---
    const [receiptList, setReceiptList] = useState([]);
    const [parentList, setParentList] = useState([]); // fetched ONCE, used only for the Create dialog dropdown
    const [approvedRegistrationList, setApprovedRegistrationList] = useState([]);
    const [classList, setClassList] = useState([]);

    // --- Loading flags ---
    const [loadingReceipts, setLoadingReceipts] = useState(false);
    const [loadingLookups, setLoadingLookups] = useState(false);

    // --- Filters ---
    const [filterReceiptNo, setFilterReceiptNo] = useState('');
    const [filterFullName, setFilterFullName] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [filterYear, setFilterYear] = useState('');

    // --- Sorting ---
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('receiptNo');

    // --- Create Receipt dialog ---
    const [openCreateDialog, setOpenCreateDialog] = useState(false);
    const [createReceiptType, setCreateReceiptType] = useState('monthly');
    const [selectedParent, setSelectedParent] = useState(null);
    const [selectedParentDetail, setSelectedParentDetail] = useState(null);
    const [loadingParentDetail, setLoadingParentDetail] = useState(false);
    const [createMonth, setCreateMonth] = useState(monthList[new Date().getMonth()].id);
    const [createYear, setCreateYear] = useState(CURRENT_YEAR);
    const [registrationAmount, setRegistrationAmount] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        fetchLookups();     // parents, approved registrations, and classes -> once, ever
        fetchReceiptList(); // receipts -> once on mount, then again only after a create
        localStorage.setItem("current_tab", "Receipt");
    }, []);

    // Parent list + class list — called ONCE on mount only
    const fetchLookups = async () => {
        setLoadingLookups(true);
        try {
            const [parentRes, batchRes, registrationRes] = await Promise.all([
                fetch(`${API_BASE}/parents`),
                fetch(`${API_BASE}/batch`),
                fetch(`${API_BASE}/register`)
            ]);
            const parentJson = await parentRes.json();
            const batchJson = await batchRes.json();
            const registrationJson = await registrationRes.json();
            setParentList(parentJson.data || []);
            setClassList(batchJson.data || []);
            setApprovedRegistrationList((registrationJson.data || []).filter((registration) => registration.status === 'Approved'));
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error (lookups)", severity: "error" });
        } finally {
            setLoadingLookups(false);
        }
    };

    // Receipt list — called on mount, and again after a receipt is created.
    // Never re-fetches parentList.
    const fetchReceiptList = async () => {
        setLoadingReceipts(true);
        try {
            const res = await fetch(`${API_BASE}/receipt`);
            const json = await res.json();
            setReceiptList(json.data || []);
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error (receipts)", severity: "error" });
        } finally {
            setLoadingReceipts(false);
        }
    };

    // --- Filtering + sorting ---
    const filteredAndSortedData = useMemo(() => {
        const receiptNoQuery = filterReceiptNo.trim().toLowerCase();
        const fullNameQuery = filterFullName.trim().toLowerCase();

        return receiptList
            .filter((r) => {
                const matchesReceiptNo = !receiptNoQuery || r.receiptNo?.toLowerCase().includes(receiptNoQuery);
                const matchesFullName = !fullNameQuery || r.fullName?.toLowerCase().includes(fullNameQuery);
                const matchesMonth = !filterMonth || r.month === filterMonth;
                const matchesYear = !filterYear || String(r.year) === String(filterYear);
                return matchesReceiptNo && matchesFullName && matchesMonth && matchesYear;
            })
            .sort((a, b) => {
                let aValue, bValue;
                if (orderBy === 'totalAmount') {
                    aValue = a.totalAmount ?? calcTotal(a.childInfo, a.receiptType);
                    bValue = b.totalAmount ?? calcTotal(b.childInfo, b.receiptType);
                } else {
                    aValue = a[orderBy] || "";
                    bValue = b[orderBy] || "";
                }
                return order === 'asc' ? (aValue < bValue ? -1 : 1) : (bValue < aValue ? -1 : 1);
            });
    }, [receiptList, filterReceiptNo, filterFullName, filterMonth, filterYear, order, orderBy]);

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const columns = [
        { id: 'receiptNo', name: 'Receipt No' },
        { id: 'fullName', name: 'Parent Name' },
        { id: 'receiptType', name: 'Type' },
        { id: 'month', name: 'Month' },
        { id: 'year', name: 'Year' },
        { id: 'totalAmount', name: 'Total Amount' }
    ];

    // --- Print an existing receipt directly from stored data (no re-fetch needed) ---
    const handlePrintReceipt = async (record) => {
        try {
            const pdfData = { fullname: record.fullName, children: record.childInfo || [] };
            const blob = await pdf(
                <ReceiptPDF
                    data={pdfData}
                    month={record.month}
                    year={record.year}
                    classList={classList}
                    receiptNo={record.receiptNo}
                    receiptDate={record.receiptDate}
                    receiptType={record.receiptType}
                />
            ).toBlob();
            const receiptLabel = record.receiptType === 'registration'
                ? 'REGISTRATION RECEIPT'
                : `RECEIPT ${record.month} ${record.year}`;
            saveAs(blob, `${record.fullName} (${receiptLabel}).pdf`);
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to generate PDF", severity: "error" });
        }
    };

    // --- Create Receipt dialog handlers ---
    const openCreate = () => {
        setCreateReceiptType('monthly');
        setSelectedParent(null);
        setSelectedParentDetail(null);
        setRegistrationAmount('');
        setCreateMonth(monthList[new Date().getMonth()].id);
        setCreateYear(CURRENT_YEAR);
        setOpenCreateDialog(true);
    };

    const handleReceiptTypeChange = (type) => {
        setCreateReceiptType(type);
        setSelectedParent(null);
        setSelectedParentDetail(null);
        setRegistrationAmount('');
    };

    const handleSelectParent = async (parent) => {
        setSelectedParent(parent);
        setSelectedParentDetail(null);
        if (!parent) return;

        if (createReceiptType === 'registration') {
            const registrationParentName = parent.father?.name || parent.mother?.name || '';
            setSelectedParentDetail({
                fullname: registrationParentName,
                children: (parent.students || []).map((student) => ({
                    fullname: student.name,
                    package: student.package
                }))
            });
            return;
        }

        setLoadingParentDetail(true);
        try {
            const res = await fetch(`${API_BASE}/parents/${parent.parentId}`);
            const json = await res.json();
            setSelectedParentDetail(json.data);
        } catch (error) {
            setSnackbar({ open: true, message: "Error loading parent details", severity: "error" });
        } finally {
            setLoadingParentDetail(false);
        }
    };

    const handleCreateReceipt = async () => {
        if (!selectedParentDetail || (createReceiptType === 'registration' && (!registrationAmount || Number(registrationAmount) < 0))) return;
        setSubmitting(true);
        try {
            const childInfo = createReceiptType === 'registration'
                ? [{
                    studentNames: selectedParentDetail.children.map((child) => child.fullname).filter(Boolean),
                    amount: Number(registrationAmount),
                    description: 'Registration Fee'
                }]
                : selectedParentDetail.children || [];
            const res = await fetch(`${API_BASE}/receipt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    fullName: selectedParentDetail.fullname,
                    month: createMonth,
                    year: createYear,
                    childInfo,
                    receiptType: createReceiptType
                })
            });
            const json = await res.json();

            if (json.status === 1) {
                // Auto-download using the data the server just returned
                // (already has the auto-generated receiptNo + receiptDate)
                const created = json.data;
                const pdfData = { fullname: created.fullName, children: created.childInfo || [] };
                const blob = await pdf(
                    <ReceiptPDF
                        data={pdfData}
                        month={created.month}
                        year={created.year}
                        classList={classList}
                        receiptNo={created.receiptNo}
                        receiptDate={created.receiptDate}
                        receiptType={created.receiptType || createReceiptType}
                    />
                ).toBlob();
                const receiptLabel = createReceiptType === 'registration'
                    ? 'REGISTRATION RECEIPT'
                    : `RECEIPT ${created.month} ${created.year}`;
                saveAs(blob, `${created.fullName} (${receiptLabel}).pdf`);

                setSnackbar({ open: true, message: `Receipt ${created.receiptNo} created`, severity: "success" });
                setOpenCreateDialog(false);
                fetchReceiptList(); // refresh ONLY the receipt list — parentList is untouched
            } else {
                setSnackbar({ open: true, message: json.message || "Failed to create receipt", severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Failed to create receipt", severity: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Receipts</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                    <Header title="Receipt Management" />
                    <Stack direction="row" spacing={1.5}>
                        <Button variant="outlined" onClick={fetchReceiptList} sx={{ borderRadius: 2 }}>
                            Refresh List
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={openCreate}
                            sx={{ borderRadius: 2, bgcolor: colors.accent, color: 'black', fontWeight: 'bold', '&:hover': { bgcolor: '#e6ac00' } }}
                        >
                            Create Receipt
                        </Button>
                    </Stack>
                </Stack>

                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3 }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                        <TextField
                            placeholder="Filter by receipt no..."
                            size="small"
                            value={filterReceiptNo}
                            onChange={(e) => setFilterReceiptNo(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 2, minWidth: 200 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        <TextField
                            placeholder="Filter by parent name..."
                            size="small"
                            value={filterFullName}
                            onChange={(e) => setFilterFullName(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 2, minWidth: 200 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                        />
                        <TextField
                            select
                            label="Month"
                            size="small"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 2, minWidth: 160 }}
                        >
                            <MenuItem value="">All Months</MenuItem>
                            {monthList.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                        </TextField>
                        <TextField
                            select
                            label="Year"
                            size="small"
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            sx={{ bgcolor: 'white', borderRadius: 2, minWidth: 140 }}
                        >
                            <MenuItem value="">All Years</MenuItem>
                            {yearList.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </TextField>
                        {(filterReceiptNo || filterFullName || filterMonth || filterYear) && (
                            <Button size="small" onClick={() => { setFilterReceiptNo(''); setFilterFullName(''); setFilterMonth(''); setFilterYear(''); }}>
                                Clear
                            </Button>
                        )}
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '60vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell key={column.id} sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleSortRequest(column.id)}
                                        >
                                            {column.name}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold' }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loadingReceipts ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}><CircularProgress size={30} /></TableCell></TableRow>
                            ) : filteredAndSortedData.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>No receipts found.</TableCell></TableRow>
                            ) : filteredAndSortedData.map((record) => (
                                <TableRow key={record.receiptId} hover>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="bold">{record.receiptNo}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: colors.accent, color: 'black', fontSize: 14 }}>
                                                {record.fullName?.split(" ")[0]?.charAt(0)}{record.fullName?.split(" ")[1]?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="subtitle2">{record.fullName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>{record.receiptType === 'registration' ? 'Registration' : 'Monthly'}</TableCell>
                                    <TableCell>{record.receiptType === 'registration' ? '—' : record.month}</TableCell>
                                    <TableCell>{record.receiptType === 'registration' ? '—' : record.year}</TableCell>
                                    <TableCell>RM{record.totalAmount ?? calcTotal(record.childInfo, record.receiptType)}</TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<FileDownload />}
                                            onClick={() => handlePrintReceipt(record)}
                                            sx={{
                                                borderRadius: 2,
                                                textTransform: 'none',
                                                borderColor: '#DDD',
                                                color: 'black',
                                                '&:hover': { borderColor: colors.accent, bgcolor: '#FFFDE7' }
                                            }}
                                        >
                                            Print
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Create Receipt Dialog */}
                <Dialog fullWidth maxWidth="sm" open={openCreateDialog} onClose={() => !submitting && setOpenCreateDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                    <DialogTitle sx={{ fontWeight: 'bold' }}>Create Receipt</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            select
                            fullWidth
                            label="Receipt Type"
                            size="small"
                            value={createReceiptType}
                            onChange={(event) => handleReceiptTypeChange(event.target.value)}
                            sx={{ mb: 2 }}
                        >
                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="registration">Registration</MenuItem>
                        </TextField>
                        <Autocomplete
                            options={createReceiptType === 'registration' ? approvedRegistrationList : parentList}
                            loading={loadingLookups}
                            getOptionLabel={(option) => createReceiptType === 'registration'
                                ? option.father?.name || option.mother?.name || ""
                                : option.fullname || ""}
                            isOptionEqualToValue={(option, value) => createReceiptType === 'registration'
                                ? option.registrationId === value.registrationId
                                : option.parentId === value.parentId}
                            value={selectedParent}
                            onChange={(e, newValue) => handleSelectParent(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={createReceiptType === 'registration' ? 'Select Approved Registration Parent' : 'Select Parent'}
                                    placeholder="Search by name..."
                                    size="small"
                                />
                            )}
                        />

                        {loadingParentDetail && (
                            <Box display="flex" justifyContent="center" p={3}><CircularProgress /></Box>
                        )}

                        {!loadingParentDetail && selectedParentDetail && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">Linked Students:</Typography>
                                {selectedParentDetail.children?.map((child, i) => (
                                    <Typography key={i} variant="body2" sx={{ ml: 1 }}>
                                        • {child.fullname}{createReceiptType === 'registration'
                                            ? (child.package ? ` (${child.package})` : '')
                                            : ` (RM${child.amount})`}
                                    </Typography>
                                ))}
                                {(!selectedParentDetail.children || selectedParentDetail.children.length === 0) && (
                                    <Typography variant="body2" sx={{ ml: 1 }} color="text.secondary">No students linked.</Typography>
                                )}

                                {createReceiptType === 'registration' ? (
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Registration Amount (RM)"
                                        size="small"
                                        value={registrationAmount}
                                        onChange={(event) => setRegistrationAmount(event.target.value)}
                                        inputProps={{ min: 0, step: '0.01' }}
                                        sx={{ mt: 3 }}
                                    />
                                ) : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">Select Month:</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={createMonth}
                                            onChange={(e) => setCreateMonth(e.target.value)}
                                        >
                                            {monthList.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
                                        </TextField>
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold">Select Year:</Typography>
                                        <TextField
                                            select
                                            fullWidth
                                            size="small"
                                            value={createYear}
                                            onChange={(e) => setCreateYear(e.target.value)}
                                        >
                                            {yearList.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                                        </TextField>
                                    </Box>
                                </Stack>}

                                <Divider sx={{ my: 2 }} />
                                <Typography fontWeight="bold">
                                    Total: RM{createReceiptType === 'registration'
                                        ? (Number(registrationAmount) || 0)
                                        : calcTotal(selectedParentDetail.children)}
                                </Typography>
                            </Box>
                        )}

                        {!selectedParent && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                Select a parent to load their student details.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setOpenCreateDialog(false)} color="inherit" disabled={submitting}>Cancel</Button>
                        <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            disabled={!selectedParentDetail
                                || (createReceiptType === 'registration'
                                    ? registrationAmount === '' || Number(registrationAmount) < 0
                                    : !createMonth || !createYear)
                                || submitting}
                            onClick={handleCreateReceipt}
                            sx={{ bgcolor: colors.accent, color: 'black', fontWeight: 'bold' }}
                        >
                            {submitting ? "Creating..." : "Create & Download"}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default ReceiptListScreen;